import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Shield, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { translations } from '../data/mockData';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

// Support both REACT_APP_API_URL (new) and REACT_APP_BACKEND_URL (legacy)
const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const { language } = useLanguage();
  const t = translations[language].checkout;
  const navigate = useNavigate();
  const { cartItems, getTotal, sessionId, clearCart, initialLoading } = useCart();
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    // Fetch available payment methods
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payment-methods`);
        const data = await response.json();
        setPaymentMethods(data.payment_methods || []);
      } catch (error) {
        console.error('Error fetching payment methods:', error);
      }
    };
    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    // Only redirect if cart is loaded and empty
    if (!initialLoading && cartItems.length === 0) {
      navigate('/');
    }
  }, [cartItems, navigate, initialLoading]);

  // Show loading state while cart is loading
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#141619] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">
            {language === 'es' ? 'Cargando carrito...' : 
             language === 'de' ? 'Warenkorb wird geladen...' : 
             'Loading cart...'}
          </p>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerInfo.name || !customerInfo.email) {
      toast.error(language === 'es' ? 'Por favor completa los campos requeridos' : 
                  language === 'de' ? 'Bitte füllen Sie die erforderlichen Felder aus' : 
                  'Please fill in required fields');
      return;
    }

    setLoading(true);

    try {
      const checkoutData = {
        session_id: sessionId,
        customer_info: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone || null
        },
        payment_method: selectedMethod
      };

      let response;
      
      if (selectedMethod === 'stripe') {
        response = await fetch(`${API_URL}/api/checkout/stripe/create-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(checkoutData)
        });
        
        const data = await response.json();
        
        if (data.checkout_url) {
          // Redirect to Stripe checkout
          window.location.href = data.checkout_url;
        } else {
          throw new Error(data.detail || 'Failed to create checkout session');
        }
      } else if (selectedMethod === 'paypal') {
        response = await fetch(`${API_URL}/api/checkout/paypal/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(checkoutData)
        });
        
        const data = await response.json();
        
        if (data.approval_url) {
          // Store order_id for capture after return
          localStorage.setItem('paypal_order_id', data.order_id);
          localStorage.setItem('paypal_paypal_order_id', data.paypal_order_id);
          // Redirect to PayPal
          window.location.href = data.approval_url;
        } else {
          throw new Error(data.detail || 'Failed to create PayPal order');
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  const isPayPalAvailable = paymentMethods.find(m => m.id === 'paypal')?.available;

  return (
    <div className="min-h-screen bg-[#141619]">
      {/* Header */}
      <header className="bg-[#1a1d21] border-b border-[#2a2d32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-mono text-sm uppercase tracking-wider">{t.backToCart}</span>
            </button>
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">{t.secure}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">{t.title}</h1>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Form */}
          <div className="space-y-8">
            {/* Customer Information */}
            <div className="bg-[#1a1d21] rounded-xl p-6 border border-[#2a2d32]">
              <h2 className="text-xl font-semibold text-white mb-6">{t.customerInfo}</h2>
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">{t.name} *</label>
                  <Input
                    type="text"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    required
                    className="bg-[#141619] border-[#2a2d32] text-white placeholder:text-[#6B7280] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="checkout-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">{t.email} *</label>
                  <Input
                    type="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    required
                    className="bg-[#141619] border-[#2a2d32] text-white placeholder:text-[#6B7280] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="checkout-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#9CA3AF] mb-2">{t.phone}</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    className="bg-[#141619] border-[#2a2d32] text-white placeholder:text-[#6B7280] focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="checkout-phone-input"
                  />
                </div>
              </form>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-[#1a1d21] rounded-xl p-6 border border-[#2a2d32]">
              <h2 className="text-xl font-semibold text-white mb-6">{t.paymentMethod}</h2>
              <div className="space-y-3">
                {/* Stripe Option */}
                <button
                  type="button"
                  onClick={() => setSelectedMethod('stripe')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    selectedMethod === 'stripe'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : 'border-[#2a2d32] hover:border-[#4a4d52]'
                  }`}
                  data-testid="payment-method-stripe"
                >
                  <div className="flex items-center gap-3">
                    <CreditCard className={`w-6 h-6 ${selectedMethod === 'stripe' ? 'text-[#D4AF37]' : 'text-[#9CA3AF]'}`} />
                    <span className={`font-medium ${selectedMethod === 'stripe' ? 'text-white' : 'text-[#9CA3AF]'}`}>
                      {t.creditCard}
                    </span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedMethod === 'stripe' ? 'border-[#D4AF37]' : 'border-[#4a4d52]'
                  }`}>
                    {selectedMethod === 'stripe' && <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />}
                  </div>
                </button>

                {/* PayPal Option */}
                <button
                  type="button"
                  onClick={() => isPayPalAvailable && setSelectedMethod('paypal')}
                  disabled={!isPayPalAvailable}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    selectedMethod === 'paypal'
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                      : !isPayPalAvailable 
                        ? 'border-[#2a2d32] opacity-50 cursor-not-allowed'
                        : 'border-[#2a2d32] hover:border-[#4a4d52]'
                  }`}
                  data-testid="payment-method-paypal"
                >
                  <div className="flex items-center gap-3">
                    <svg className={`w-6 h-6 ${selectedMethod === 'paypal' ? 'text-[#D4AF37]' : 'text-[#9CA3AF]'}`} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.773.773 0 0 1 .762-.642h6.927c2.287 0 4.145.613 5.389 1.773 1.229 1.146 1.858 2.794 1.818 4.77-.003.15-.009.3-.018.45-.224 3.82-2.78 5.919-6.816 5.919H9.807a.958.958 0 0 0-.945.808l-.9 5.691a.642.642 0 0 1-.633.545l-.253-.003z"/>
                      <path d="M20.07 8.592c-.016.135-.034.27-.055.407-.776 4.93-4.276 6.613-8.508 6.613h-2.15a1.042 1.042 0 0 0-1.03.882l-1.1 6.968-.312 1.976a.548.548 0 0 0 .542.632h3.807a.913.913 0 0 0 .902-.77l.037-.193.714-4.532.046-.25a.913.913 0 0 1 .902-.77h.568c3.673 0 6.547-1.491 7.387-5.806.35-1.8.169-3.301-.758-4.358a3.62 3.62 0 0 0-1.042-.799z"/>
                    </svg>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${selectedMethod === 'paypal' ? 'text-white' : 'text-[#9CA3AF]'}`}>
                        {t.paypal}
                      </span>
                      {!isPayPalAvailable && (
                        <span className="text-xs px-2 py-0.5 bg-[#2a2d32] rounded text-[#6B7280]">
                          {t.paypalComingSoon}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedMethod === 'paypal' ? 'border-[#D4AF37]' : 'border-[#4a4d52]'
                  }`}>
                    {selectedMethod === 'paypal' && <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />}
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div>
            <div className="bg-[#1a1d21] rounded-xl p-6 border border-[#2a2d32] sticky top-8">
              <h2 className="text-xl font-semibold text-white mb-6">{t.orderSummary}</h2>
              
              {/* Items */}
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex justify-between items-start pb-4 border-b border-[#2a2d32]">
                    <div className="flex-1">
                      <h3 className="font-medium text-white">{item.name}</h3>
                      <p className="text-sm text-[#9CA3AF]">x{item.quantity}</p>
                    </div>
                    <span className="text-white font-medium">
                      €{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 mb-8">
                <div className="flex justify-between text-[#9CA3AF]">
                  <span>{t.subtotal}</span>
                  <span>€{getTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-white pt-3 border-t border-[#2a2d32]">
                  <span>{t.total}</span>
                  <span className="text-[#D4AF37]">€{getTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Pay Button */}
              <Button
                onClick={handleSubmit}
                disabled={loading || cartItems.length === 0}
                className="w-full py-6 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619] font-bold text-lg rounded-full transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="checkout-pay-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.processing}
                  </span>
                ) : (
                  t.pay
                )}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Checkout;
